import {
  AuthAccountJobPositionAssignmentStatusChoices,
  ClockIcon,
  ConfirmModal,
  CopyIcon,
  FlexCalendarCalendarEventStatusChoices,
  handleApiRequest,
  LinkIcon,
  UpdateJobPositionAssignmentStatusMutation,
  UpdateJobPositionAssignmentStatusMutationVariables,
  User2Icon,
  useUpdateJobPositionAssignmentStatusMutation,
} from "@cpj-ai/panel-npm-package";
import { Button, Chip, Divider } from "@heroui/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { InterviewType } from "./types";

interface InterviewCardProps {
  interview: InterviewType;
  isUpcoming: boolean;
  isNext?: boolean;
  companyName?: string;
  interviewUrl?: string | null;
  assignmentId?: string;
  refetch?: () => void;
}

const InterviewCard: React.FC<InterviewCardProps> = ({
  interview,
  isUpcoming,
  isNext = false,
  companyName,
  interviewUrl,
  assignmentId,
  refetch,
}) => {
  const [updateJobPositionAssignmentStatus, { loading }] = useUpdateJobPositionAssignmentStatusMutation();
  const [isOpenConfirmCancelInterview, setIsOpenConfirmCancelInterview] = useState(false);
  const [copied, setCopied] = useState(false);
  const interviewDate = new Date(interview.interviewDate);
  const now = new Date();

  const daysUntil = Math.ceil((interviewDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const getDaysUntilText = () => {
    if (daysUntil === 0) return "Today";
    if (daysUntil === 1) return "Tomorrow";
    if (daysUntil > 1) return `In ${daysUntil} days`;
    return `${Math.abs(daysUntil)} days ago`;
  };

  const getTimingColor = () => {
    if (!isUpcoming) return "default";
    if (daysUntil === 0) return "danger";
    if (daysUntil <= 2) return "warning";
    return "primary";
  };

  const getStatusColor = () => {
    switch (interview.status) {
      case FlexCalendarCalendarEventStatusChoices.CANCELED:
        return "danger";
      case FlexCalendarCalendarEventStatusChoices.COMPLETED:
        return "success";
      case FlexCalendarCalendarEventStatusChoices.SCHEDULED:
        return "warning";
      case FlexCalendarCalendarEventStatusChoices.STARTED:
        return "success";
      default:
        return "default";
    }
  };

  const copyInterviewLink = () => {
    if (!interviewUrl) return;
    navigator.clipboard.writeText(interviewUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleJoinInterview = () => {
    if (!interviewUrl) return;
    window.open(interviewUrl, "_blank");
  };

  const handleConfirmCancelInterview = async () => {
    if (!assignmentId) {
      toast.error("No assignment ID provided");
      return;
    }

    await handleApiRequest<
      UpdateJobPositionAssignmentStatusMutation,
      UpdateJobPositionAssignmentStatusMutationVariables
    >({
      apiFunction: updateJobPositionAssignmentStatus,
      variables: {
        id: assignmentId,
        status: AuthAccountJobPositionAssignmentStatusChoices.INTERVIEW_CANCELED,
      },
      componentName: "CandidateHiringProcess",
      actionName: "updateAssignmentStatus",
      successMessage: "Interview canceled successfully",
      fallbackErrorMessage: "Failed to cancel interview. Please try again later.",
      onSuccess: refetch,
      // extraErrorContext: {
      //   assignmentId,
      //   newStatus: status,
      // },
    });
  };

  return (
    <div
      className={`border ${isNext ? "border-2 border-primary" : isUpcoming ? "border-primary-200" : ""} 
        rounded-xl overflow-hidden 
        ${isNext ? "shadow-sm" : isUpcoming ? "shadow-xs" : "bg-gray-50/50"}`}
    >
      <div
        className={`flex items-center p-4 
          ${isNext ? "bg-primary-50" : isUpcoming ? "bg-primary-50/50" : "bg-gray-100/80"}`}
      >
        <div
          className={`flex-shrink-0 ${isNext ? "w-14 h-14" : "w-12 h-12"} 
            ${isUpcoming ? "bg-primary-100 text-primary-600" : "bg-gray-200 text-gray-600"} 
            rounded-md flex items-center justify-center mr-4
            ${isNext ? "border border-primary-200" : ""}`}
        >
          <div className="text-center">
            <div className="font-bold text-sm">{interviewDate.getDate()}</div>
            <div className="text-xs">{interviewDate.toLocaleString("default", { month: "short" })}</div>
          </div>
        </div>
        <div className="flex-1 flex flex-col md:flex-row justify-between">
          <div className="flex items-center justify-between md:justify-start gap-2 mb-2 md:mb-0">
            <h3 className="text-medium md:text-lg font-semibold text-gray-800">{interview.type || "Interview"}</h3>
            <Chip color={getStatusColor()} size="sm" variant={isNext ? "flat" : undefined} className="capitalize">
              {interview.status.toLowerCase()}
            </Chip>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
              <ClockIcon className={`w-4 h-4 ${isUpcoming ? "fill-primary-500" : "fill-gray-600"}`} />
              <span>
                {interviewDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
                {" — "}
                {new Date(new Date(interview.interviewDate).getTime() + 60 * 60 * 1000).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
              <User2Icon
                className={`w-5 h-5 -ms-1 ${isUpcoming ? "stroke-primary-500" : "stroke-gray-600"}`}
                viewBox="0 0 20 20"
              />
              <span className="mt-1">
                with <span className="font-medium">{companyName || "Interviewer"}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap justify-between mb-2 gap-y-2">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-gray-500 mr-2">Location</span>
              <span>{interview.location || "Online"}</span>
            </div>
            <div>
              <span className="text-gray-500 mr-2">Created by</span>
              <span>Organization</span>
            </div>
          </div>
          {isUpcoming && (
            <div>
              <Chip color={getTimingColor()} size="sm">
                {getDaysUntilText()}
              </Chip>
            </div>
          )}
        </div>

        {isUpcoming && (
          <>
            <Divider className="my-3" />

            <div className="flex items-center justify-between mb-4 bg-gray-50 p-2 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-full bg-blue-100 text-blue-600">
                  <LinkIcon className="w-4 h-4" />
                </div>
                <span className="text-sm text-gray-700 font-medium">Meeting Link</span>
              </div>
              <Button
                size="sm"
                color={copied ? "success" : "primary"}
                variant="bordered"
                radius="lg"
                startContent={
                  copied ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  ) : (
                    <CopyIcon className="w-4 h-4 fill-primary-500" />
                  )
                }
                onPress={copyInterviewLink}
              >
                {copied ? "Copied" : "Copy Link"}
              </Button>
            </div>

            <div
              className={`flex flex-col md:flex-row gap-4 md:gap-16 ${isNext ? "justify-between items-center" : "justify-end"}`}
            >
              <div className="w-full">
                {isNext && (
                  <div className="text-sm text-gray-500 mt-2 w-full">
                    Please prepare your portfolio and be ready 5 minutes before the interview starts.
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 w-full md:w-auto">
                <Button
                  size="sm"
                  color="danger"
                  variant="bordered"
                  radius="lg"
                  className="w-full md:w-auto"
                  onPress={() => setIsOpenConfirmCancelInterview(true)}
                  isLoading={loading}
                >
                  Cancel Interview
                </Button>
                {daysUntil <= 1 && (
                  <Button
                    size="sm"
                    color="primary"
                    variant="solid"
                    radius="lg"
                    className="w-full md:w-auto"
                    onPress={handleJoinInterview}
                  >
                    Join
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        isOpen={isOpenConfirmCancelInterview}
        onClose={() => setIsOpenConfirmCancelInterview(false)}
        onConfirm={handleConfirmCancelInterview}
        title="Confirm Cancel Interview"
        message="Are you sure you want to cancel this interview?"
        variant="destructive"
      />
    </div>
  );
};

export default InterviewCard;
